import {createStyles} from '@material-ui/core/styles';
import {FONT_FAMILY, primaryColor, styleColors} from 'src/main.style';
import {TODO_SEEN_HIGHLIGHT_DURATION} from './useTodoSeen';

export const TodoListRowStyle = () => createStyles({
  row: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    minHeight: '112px',
    width: '100%',
    boxSizing: 'border-box',
    paddingRight: '12px',
    backgroundColor: styleColors.white,
    color: styleColors.greyDark2,
    position: 'relative',
    transition: 'opacity 180ms ease-out',
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      backgroundColor: primaryColor,
      opacity: 0,
      pointerEvents: 'none',
    },
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
      '&$recentlySeen::before, &$recentlySeen $seenBy': {
        animation: 'none',
      },
      '& $title, & $hideButtonSlot, & $metadataWithDate': {
        transition: 'none',
      },
    },
  },
  hiding: {
    opacity: 0,
    pointerEvents: 'none',
  },
  visibleUnseen: {
    '&::before': {
      opacity: 0.1,
    },
  },
  recentlySeen: {
    '&::before': {
      opacity: 0.1,
      animation: `$seenHighlight ${TODO_SEEN_HIGHLIGHT_DURATION}ms ease-out forwards`,
    },
    '& $seenBy': {
      animation: '$seenLabelIn 500ms ease-out',
    },
  },
  '@keyframes seenHighlight': {
    '0%, 25%': {opacity: 0.14},
    '100%': {opacity: 0},
  },
  '@keyframes seenLabelIn': {
    from: {opacity: 0},
    to: {opacity: 1},
  },
  details: {
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'stretch',
    flex: 1,
    minWidth: 0,
    padding: '14px 8px 14px 0',
    border: 0,
    background: 'transparent',
    color: 'inherit',
    fontFamily: FONT_FAMILY,
    fontWeight: 'inherit',
    lineHeight: 'normal',
    letterSpacing: 'normal',
    textAlign: 'left',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    '&:focus-visible': {
      outline: `2px solid ${primaryColor}`,
      outlineOffset: '-3px',
      borderRadius: '8px',
    },
  },
  detailsLabel: {
    gap: 0,
    textAlign: 'left',
  },
  avatarRail: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '88px',
    flexShrink: 0,
  },
  avatarGroup: {
    display: 'flex',
    alignItems: 'center',
  },
  extraAssignees: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    marginLeft: '-10px',
    borderRadius: '50%',
    border: `2px solid ${styleColors.white}`,
    backgroundColor: styleColors.greyLight2,
    color: styleColors.greyDark2,
    fontSize: '10px',
    fontWeight: 600,
    zIndex: 1,
  },
  unassignedAvatar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '52px',
    height: '52px',
    borderRadius: '50%',
    backgroundColor: styleColors.greyLight2,
    color: styleColors.greyMedium3,
    '& svg': {
      fontSize: '28px',
    },
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    flex: 1,
    minWidth: 0,
  },
  title: {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    overflow: 'hidden',
    overflowWrap: 'anywhere',
    fontSize: '16px',
    lineHeight: '21px',
    fontWeight: 500,
    transition: 'color 1600ms ease-out',
  },
  unseenTitle: {
    color: primaryColor,
  },
  completedTitle: {
    color: styleColors.greyMedium3,
    textDecoration: 'line-through',
  },
  assignee: {
    color: styleColors.greyDark1,
    fontSize: '12px',
    lineHeight: '17px',
    overflowWrap: 'anywhere',
  },
  assigneeNames: {
    color: styleColors.greyDark2,
    fontWeight: 600,
  },
  preview: {
    display: 'block',
    color: styleColors.greyDark1,
    fontSize: '14px',
    fontWeight: 300,
    lineHeight: '19px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  metadata: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '5px 10px',
    color: styleColors.greyDark1,
    fontSize: '10px',
    lineHeight: '14px',
  },
  metadataWithDate: {
    // Extend the footer across the 44px checkbox and the details' 8px right padding.
    marginRight: '-52px',
    transition: 'margin-right 240ms ease-out',
  },
  due: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '3px',
    marginLeft: 'auto',
    whiteSpace: 'nowrap',
    color: styleColors.greyDark1,
    fontSize: '10px',
    lineHeight: '14px',
    '& svg': {
      width: '12px',
      height: '12px',
    },
  },
  dateDue: {
    color: primaryColor,
  },
  seenBy: {
    display: 'inline-flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '2px',
  },
  unseen: {
    color: primaryColor,
  },
  actions: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'stretch',
    justifyContent: 'center',
    flexShrink: 0,
    position: 'relative',
    pointerEvents: 'none',
  },
  actionsWithDate: {
    paddingBottom: '24px',
    boxSizing: 'border-box',
  },
  completionControl: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '44px',
    height: '44px',
    flexShrink: 0,
    position: 'relative',
    cursor: 'pointer',
    pointerEvents: 'auto',
  },
  completionInput: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    margin: 0,
    opacity: 0,
    cursor: 'pointer',
    zIndex: 1,
    '&:focus-visible + $completionCircle': {
      outline: `2px solid ${primaryColor}`,
      outlineOffset: '3px',
    },
  },
  completionCircle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    boxSizing: 'border-box',
    border: `1.5px solid ${styleColors.greyMedium3}`,
    borderRadius: '50%',
    color: '#FFFFFF',
    transition: 'background-color 150ms, border-color 150ms',
    '& svg': {
      fontSize: '19px',
    },
  },
  completionChecked: {
    backgroundColor: primaryColor,
    borderColor: primaryColor,
  },
  hideButtonSlot: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    width: 0,
    height: '44px',
    overflow: 'hidden',
    opacity: 0,
    transition: 'width 240ms ease-out, opacity 180ms ease-out',
  },
  hideButtonVisible: {
    '& $hideButtonSlot': {
      width: '60px',
      opacity: 1,
    },
    '& $metadataWithDate': {
      // Keep the footer aligned as the 52px button and its 8px gap expand.
      marginRight: '-112px',
    },
  },
  hideButton: {
    flexShrink: 0,
    marginLeft: '8px',
    pointerEvents: 'auto',
    whiteSpace: 'nowrap',
    '&&': {
      minWidth: '52px',
      width: '52px',
      height: '28px',
      padding: '0 8px',
    },
    // Extend the touch target to 44px without changing the shared button's border.
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: '-8px 0',
    },
  },
});



// WEBPACK FOOTER //
// ./src/components/todo/TodoListRow.style.ts